import { computed, ref, watch, Ref, ComputedRef } from "vue";
import { useToast } from "vue-toastification";
import { useIsDesktop } from "@/features/useIsDesktop";

import { BuilderFeatures } from "@/enums/BuilderFeatures";
import { BuilderSliderSteps } from "@/enums/BuilderSliderSteps";

import { eventTracking } from "@/helpers/googleAnalyticsEventTracking";

import store from "@/store";

import type { BuilderInterfaceWithHouseDesigns } from "@/types/BuilderInterface";
import type { HouseDesignInterface } from "@/types/HouseDesignInterface";
import {
  EventActionName,
  EventByAction,
  EventByCategory,
  EventLabelByName,
} from "@/types/EventTracking";
import { UserQuoteSelectionInterface } from "@/types/UserQuoteSelectionInterface";
import {
  UseBuilderEstimatorSelectionProps,
  UseBuilderEstimatorSelectionReturn,
} from "@/types/useBuilderEstimatorSelectionInterface";
import { ColorSelection } from "@/types/colorSelectionInterface";

export function useBuilderEstimatorSelectionHouse(
  props: UseBuilderEstimatorSelectionProps
): UseBuilderEstimatorSelectionReturn {
  const currentFeature = ref<BuilderFeatures>(BuilderFeatures.FEATURES);
  const currentSliderStepIndex = ref<number>(0);
  const isImageFloorplanModalOpen = ref<boolean>(false);
  const showFloorplan = ref<boolean>(false);
  const sliderImages = ref<string[]>([]);
  const sliderSteps = ref<BuilderSliderSteps[]>([
    ...Object.values(BuilderSliderSteps),
  ]);
  const zoom = ref<number>(1);

  const { isDesktop } = useIsDesktop(990);

  const backStep = computed(
    () => sliderSteps.value[currentSliderStepIndex.value - 1]
  );

  const currentSliderStep = computed(
    () => sliderSteps.value[currentSliderStepIndex.value]
  );

  const getColorsSelection = computed(() => {
    const { kitchens, bathroom } = house.value;

    if (currentSliderStep.value === BuilderSliderSteps.BATHROOM) {
      return bathroom.options;
    }

    if (currentSliderStep.value === BuilderSliderSteps.KITCHEN) {
      return kitchens;
    }

    return [];
  });

  const house = computed(
    () => props.selectedHouseDesign ?? props.selectedBuilder?.houses[0]
  );

  const nextStep = computed(
    () => sliderSteps.value[currentSliderStepIndex.value + 1]
  );

  const selectedBuilder = computed(() => props.selectedBuilder);

  const selectedQuote = computed(
    () => store.getters["builder/getSelectedSelection"]
  );

  const showBrowseInteriorsText = computed(() => {
    return (
      isImageAvailable(BuilderSliderSteps.KITCHEN) ||
      isImageAvailable(BuilderSliderSteps.BATHROOM) ||
      isImageAvailable(BuilderSliderSteps.LIVING)
    );
  });

  const showColorsControls = computed(() => {
    const { BATHROOM, KITCHEN } = BuilderSliderSteps;
    const {
      bathroom: { options },
      kitchens,
    } = house.value;

    const isBathroomStep = currentSliderStep.value === BATHROOM;
    const isKitchenStep = currentSliderStep.value === KITCHEN;

    return (
      (isBathroomStep && options.length > 1) ||
      (isKitchenStep && kitchens.length > 1)
    );
  });

  const showExitInteriorsButton = computed(() => {
    const { KITCHEN, BATHROOM, LIVING } = BuilderSliderSteps;

    return [KITCHEN, BATHROOM, LIVING].includes(currentSliderStep.value);
  });

  const showSliderArrows = computed(() => {
    const allowedSteps = [BuilderSliderSteps.LIVING];

    return allowedSteps.includes(currentSliderStep.value);
  });

  const closeImageModal = () => {
    isImageFloorplanModalOpen.value = false;
    zoom.value = 1;
  };

  const exitInteriors = () => {
    if (showFloorplan.value) {
      showFloorplan.value = false;
    } else {
      jumpToStep(BuilderSliderSteps.MAIN_IMAGE);
    }
  };

  const getImagesUrls = (
    selectedUrl: string | undefined,
    images: { url: string }[]
  ): string[] => {
    return selectedUrl ? [selectedUrl] : images.map(({ url }) => url);
  };

  const handleStepChange = (stepChange: number) => {
    currentSliderStepIndex.value += stepChange;
  };

  const initializeHouseDesign = () => {
    if (props.selectedHouseDesign) {
      setSliderImages();
      resetSteps();
    }
  };

  const isImageAvailable = (step: BuilderSliderSteps): boolean => {
    if (!house.value) return false;

    switch (step) {
      case BuilderSliderSteps.KITCHEN:
        return house.value.kitchens.some(
          (kitchen) => kitchen.url && kitchen.url.trim() !== ""
        );
      case BuilderSliderSteps.BATHROOM:
        return house.value.bathroom.options.some(
          (option) => option.url && option.url.trim() !== ""
        );
      case BuilderSliderSteps.LIVING:
        return house.value.loungeRoom.urls.some(
          (url) => url && url.trim() !== ""
        );
      default:
        return false;
    }
  };

  const jumpToStep = (step: BuilderSliderSteps) => {
    const stepIndex = sliderSteps.value.indexOf(step);

    if (stepIndex !== -1) {
      currentSliderStepIndex.value = stepIndex;
    }
  };

  const onBackStep = () => {
    if (currentSliderStepIndex.value === 0) {
      return;
    }

    handleStepChange(-1);
  };

  const onChangeImage = () => {
    showFloorplan.value = true;
  };

  const onSelectColor = (userSelection: ColorSelection) => {
    sliderImages.value = [userSelection.url];
  };

  const onSelectHouseImage = (houseId: string): { houseDesignId: string } => {
    return { houseDesignId: houseId };
  };

  const openFloorplanImageModal = () => {
    isImageFloorplanModalOpen.value = true;
  };

  const resetSteps = () => {
    handleStepChange(-currentSliderStepIndex.value);
  };

  const setCurrentFeature = (feature: BuilderFeatures) => {
    currentFeature.value = feature;
  };

  const setSliderImages = () => {
    if (!house.value) {
      return;
    }

    const {
      facades,
      kitchens,
      bathroom,
      loungeRoom,
      mainImages,
    }: HouseDesignInterface = house.value;

    switch (currentSliderStep.value) {
      case BuilderSliderSteps.MAIN_IMAGE:
        sliderImages.value = mainImages;
        break;
      case BuilderSliderSteps.SELECTION:
        sliderImages.value = getImagesUrls(
          selectedQuote.value.facade?.url,
          facades
        );
        break;
      case BuilderSliderSteps.LIVING:
        sliderImages.value = loungeRoom.urls;
        break;
      case BuilderSliderSteps.KITCHEN:
        sliderImages.value = getImagesUrls(
          selectedQuote.value.kitchen?.url,
          kitchens
        );
        break;
      case BuilderSliderSteps.BATHROOM:
        sliderImages.value = getImagesUrls(
          selectedQuote.value.bathroom?.url,
          bathroom.options
        );
        break;
      default:
        break;
    }
  };

  const trackHouseDesignClick = (houseDesign: HouseDesignInterface) => {
    eventTracking({
      actionsName: EventActionName.HOUSE_DESIGN_ON_CLICK,
      eventByCategory: EventByCategory.ON_CLICK,
      eventLabelByName: EventLabelByName.CLICK_HOUSE_DESIGN,
      eventByAction: EventByAction.CLICK,
      houseDesignId: houseDesign._id,
      houseDesignName: houseDesign.name,
    });
  };

  const updateZoom = (value: number) => {
    const newZoom = zoom.value + value;
    const MIN_ZOOM_LEVEL = 1;
    const MAX_ZOOM_LEVEL = 2.6;

    if (newZoom >= MIN_ZOOM_LEVEL && newZoom <= MAX_ZOOM_LEVEL) {
      zoom.value = newZoom;
    }
  };

  watch(currentSliderStepIndex, () => {
    setSliderImages();
  });

  watch(
    () => props,
    () => {
      setSliderImages();
      resetSteps();
    },
    { deep: true, immediate: true }
  );

  return {
    currentFeature,
    currentSliderStepIndex,
    isImageFloorplanModalOpen,
    showFloorplan,
    sliderImages,
    sliderSteps,
    zoom,
    isDesktop,

    backStep,
    currentSliderStep,
    getColorsSelection,
    house,
    nextStep,
    selectedBuilder,
    selectedQuote,
    showBrowseInteriorsText,
    showColorsControls,
    showExitInteriorsButton,
    showSliderArrows,

    closeImageModal,
    exitInteriors,
    getImagesUrls,
    handleStepChange,
    initializeHouseDesign,
    isImageAvailable,
    jumpToStep,
    onBackStep,
    onChangeImage,
    onSelectColor,
    onSelectHouseImage,
    openFloorplanImageModal,
    resetSteps,
    setCurrentFeature,
    setSliderImages,
    trackHouseDesignClick,
    updateZoom,
  };
}
